import React, { useEffect, useMemo, useState } from "react";
import Modal from "../../design-system/components/modals/Modal";
import { PrimaryButton, SecondaryButton } from "../../design-system/components/buttons";
import { typography, textColors, borders, surfaces } from "../../design-system/tokens";
import { apiFetch } from "../../core/utils/api";

import { loadStripe } from "@stripe/stripe-js";
import { AddressElement, Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";

const API_ENDPOINTS = {
  STRIPE_SUBSCRIPTION_START: "/api/stripe/subscription/start/",
  STRIPE_SUBSCRIPTION_CONFIRM: "/api/stripe/subscription/confirm/",
  CURRENT_SUBSCRIPTION: "/api/subscription/current/",
  STRIPE_CONFIG: "/api/stripe/config/",
};

const getStripePublishableKey = () =>
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_STRIPE_PUBLIC_KEY ||
  process.env.REACT_APP_STRIPE_PUBLISHABLE ||
  "";

const SafeError = ({ message }) =>
  message ? <div className={`${typography.bodySmall} text-red-600 dark:text-red-400`}>{message}</div> : null;

const formatMoneyFromMinor = (amountMinor, currency) => {
  const amt = Number(amountMinor);
  if (!Number.isFinite(amt)) return null;
  const cur = (currency || "USD").toUpperCase();
  // Stripe amounts are in the smallest unit (cents for USD).
  const major = amt / 100.0;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: cur,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(major);
  } catch {
    return `${major.toFixed(2)} ${cur}`;
  }
};

const formatUnixDate = (unixSeconds) => {
  const n = Number(unixSeconds);
  if (!Number.isFinite(n) || n <= 0) return null;
  try {
    return new Date(n * 1000).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return null;
  }
};

const getDiscountKinds = (pricing) =>
  new Set(
    (pricing?.discounts || [])
      .map((d) => String(d?.kind || "").trim().toLowerCase())
      .filter(Boolean)
  );

const getFirstInvoiceOnlyNote = ({ now, later }) => {
  const nowKinds = getDiscountKinds(now);
  if (nowKinds.size === 0) return null;

  const laterKinds = getDiscountKinds(later);
  const hasCoupon = nowKinds.has("coupon");
  const hasDiscount = nowKinds.has("global") || nowKinds.has("discount");
  const laterHasCoupon = laterKinds.has("coupon");
  const laterHasDiscount = laterKinds.has("global") || laterKinds.has("discount");

  const missingCoupon = hasCoupon && !laterHasCoupon;
  const missingDiscount = hasDiscount && !laterHasDiscount;

  if (missingCoupon && missingDiscount) return "Coupon and discount apply to the first invoice only.";
  if (missingCoupon) return "Coupon applies to the first invoice only.";
  if (missingDiscount) return "Discount applies to the first invoice only.";
  return null;
};

const StepPill = ({ n, label }) => (
  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${borders.default} ${surfaces.secondary}`}>
    <span className={`${typography.caption} ${textColors.secondary}`}>{n}</span>
    <span className={`${typography.caption} ${textColors.primary} font-medium`}>{label}</span>
  </div>
);

const SubscriptionPaymentForm = ({ disabled, onSuccess, onError, onRequestCancel, confirmReturnUrl, mode = "payment" }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    onError?.(null);
    try {
      const return_url = confirmReturnUrl || window.location.href;
      const res =
        mode === "setup"
          ? await stripe.confirmSetup({
              elements,
              confirmParams: { return_url },
              redirect: "if_required",
            })
          : await stripe.confirmPayment({
              elements,
              confirmParams: { return_url },
              redirect: "if_required",
            });
      if (res?.error) {
        onError?.(res.error.message || "Payment failed. Please try again.");
        return;
      }

      const intent = mode === "setup" ? res?.setupIntent : res?.paymentIntent;
      const status = intent?.status || "";
      if (status === "succeeded" || status === "processing") {
        onSuccess?.({ intent_status: status, intent_type: mode });
        return;
      }
      if (status === "requires_action") {
        onError?.("Additional authentication is required. Please try again.");
        return;
      }
      if (status === "requires_payment_method") {
        onError?.("Your payment method was not accepted. Please try a different card.");
        return;
      }
      if (!status || status === "canceled" || status === "requires_confirmation") {
        onError?.("Payment could not be completed. Please try again.");
        return;
      }
      onSuccess?.({ intent_status: status, intent_type: mode });
    } catch (e) {
      onError?.(e?.message || "Payment failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className={`rounded-lg border ${borders.default} ${surfaces.primary} p-4`}>
        <PaymentElement />
      </div>
      <div className="flex items-center justify-end gap-3">
        <SecondaryButton type="button" size="sm" onClick={() => onRequestCancel?.()} disabled={disabled || submitting}>
          Cancel
        </SecondaryButton>
        <PrimaryButton size="sm" onClick={handleConfirm} loading={submitting} disabled={disabled || !stripe || !elements}>
          {mode === "setup" ? "Save & Activate" : "Pay & Activate"}
        </PrimaryButton>
      </div>
    </div>
  );
};

const SubscriptionPaymentModal = ({
  isOpen,
  onClose,
  planName,
  billingPeriod,
  couponCode,
  onSubscriptionUpdated, // optional: parent can update local subscription state
  onActivated, // optional: called with { planName, billingPeriod } when subscription is successfully activated
  notice, // optional ReactNode
}) => {
  const [loading, setLoading] = useState(false);
  const [startError, setStartError] = useState(null);
  const [confirmError, setConfirmError] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [started, setStarted] = useState(null); // { status, subscription_id, payment_intent_client_secret, discount_applied }
  const [successMessage, setSuccessMessage] = useState(null);
  const [taxLocationTouched, setTaxLocationTouched] = useState(false);
  const [taxAddressComplete, setTaxAddressComplete] = useState(false);
  const [taxAddressValue, setTaxAddressValue] = useState(null); // AddressElement value

  const isDarkMode = typeof document !== "undefined" && document.documentElement.classList.contains("dark");
  const [publishableKey, setPublishableKey] = useState(() => getStripePublishableKey());
  const stripePromise = useMemo(() => (publishableKey ? loadStripe(publishableKey) : null), [publishableKey]);

  const normalizedTaxCountry = String(taxAddressValue?.address?.country || "").trim().toUpperCase();
  const normalizedTaxPostalCode = String(taxAddressValue?.address?.postal_code || "").trim();
  const hasTaxLocation = Boolean(taxAddressComplete) && Boolean(normalizedTaxCountry) && Boolean(normalizedTaxPostalCode);
  const canStart = Boolean(planName) && Boolean(billingPeriod) && hasTaxLocation;

  useEffect(() => {
    if (!isOpen) return;
    // Reset state each open so the modal is deterministic.
    setLoading(false);
    setStartError(null);
    setConfirmError(null);
    setCancelConfirmOpen(false);
    setStarted(null);
    setSuccessMessage(null);
    setTaxLocationTouched(false);
    setTaxAddressComplete(false);
    setTaxAddressValue(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!canStart) return;

    let cancelled = false;
    const start = async () => {
      setLoading(true);
      setStartError(null);
      try {
        const resp = await apiFetch(API_ENDPOINTS.STRIPE_SUBSCRIPTION_START, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            plan_name: planName,
            billing_period: billingPeriod,
            coupon_code: couponCode || undefined,
            // Stripe Tax: collect minimal location before creating the subscription so the first invoice includes tax.
            customer_location: { country: normalizedTaxCountry, postal_code: normalizedTaxPostalCode },
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data?.error || "Failed to start payment. Please try again.");
        }
        if (!cancelled) setStarted(data);

        // If backend returns no client secret at all, it may still be able to activate immediately.
        // Only show "activated" immediately for the explicit fallback statuses.
        const hasAnySecret = Boolean(data?.payment_intent_client_secret || data?.setup_intent_client_secret);
        if (!hasAnySecret && (data?.status === "trial_started" || data?.status === "created")) {
          // Refresh subscription state best-effort.
          try {
            const subResp = await apiFetch(API_ENDPOINTS.CURRENT_SUBSCRIPTION, { headers: { "Content-Type": "application/json" } });
            const subData = await subResp.json().catch(() => null);
            if (subResp.ok) onSubscriptionUpdated?.(subData);
          } catch {}
          if (!cancelled) {
            onClose?.();
            onActivated?.({ planName, billingPeriod });
          }
        }
      } catch (e) {
        if (!cancelled) setStartError(e?.message || "Failed to start payment.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    start();
    return () => {
      cancelled = true;
    };
  }, [isOpen, canStart, planName, billingPeriod, couponCode, normalizedTaxCountry, normalizedTaxPostalCode, onSubscriptionUpdated]);

  const paymentClientSecret = started?.payment_intent_client_secret || null;
  const setupClientSecret = started?.setup_intent_client_secret || null;
  const effectiveClientSecret = paymentClientSecret || setupClientSecret;
  const mode = setupClientSecret ? "setup" : "payment";
  const showPaymentElement = Boolean(effectiveClientSecret);

  const pricingNow = started?.pricing?.now || null;
  const pricingUpcoming = started?.pricing?.upcoming || null;
  const pricingAfterTrial = started?.pricing?.after_trial || null;
  const pricingCurrency = pricingNow?.currency || pricingAfterTrial?.currency || pricingUpcoming?.currency || "USD";
  const trialEnd = started?.pricing?.trial_end || null;
  const laterPricing = trialEnd ? pricingAfterTrial || pricingUpcoming : pricingUpcoming;
  const firstInvoiceOnlyNote = getFirstInvoiceOnlyNote({ now: pricingNow, later: laterPricing });
  const stripeAppearance = useMemo(
    () => ({
      theme: isDarkMode ? "night" : "stripe",
      variables: {
        colorPrimary: isDarkMode ? "#60a5fa" : "#2563eb",
        colorBackground: isDarkMode ? "#121212" : "#ffffff",
        colorText: isDarkMode ? "#d4d4d4" : "#111827",
        colorTextSecondary: isDarkMode ? "#a3a3a3" : "#4b5563",
        colorTextPlaceholder: isDarkMode ? "#737373" : "#9ca3af",
        colorBorder: isDarkMode ? "#2a2a2a" : "#e5e7eb",
      },
      rules: {
        ".Input": {
          backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
        },
      },
    }),
    [isDarkMode]
  );

  useEffect(() => {
    // Fetch Stripe publishable key as soon as the modal opens so we can render AddressElement
    // before we have a clientSecret (needed for tax location collection).
    if (!isOpen) return;
    if (publishableKey) return;
    let cancelled = false;

    const loadKey = async () => {
      try {
        const resp = await apiFetch(API_ENDPOINTS.STRIPE_CONFIG, { headers: { "Content-Type": "application/json" } });
        const data = await resp.json().catch(() => ({}));
        const key = String(data?.publishable_key || "").trim();
        if (!cancelled && resp.ok && key) setPublishableKey(key);
      } catch {
        // ignore; UI will show fallback error when Stripe Elements are needed
      }
    };

    loadKey();
    return () => {
      cancelled = true;
    };
  }, [isOpen, publishableKey]);

  const requestCancel = () => {
    if (loading) return;
    setCancelConfirmOpen(true);
  };

  const confirmCancel = () => {
    setCancelConfirmOpen(false);
    onClose?.();
  };

  return (
    <>
      <Modal
        isOpen={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
        title="Cancel activation?"
        size="sm"
        closeOnOverlayClick
        closeOnEsc
        showCloseButton
        footer={
          <>
            <PrimaryButton type="button" onClick={() => setCancelConfirmOpen(false)}>
              No, go back
            </PrimaryButton>
            <SecondaryButton type="button" onClick={confirmCancel}>
              Yes, cancel
            </SecondaryButton>
          </>
        }
      >
        <div className={`${typography.bodySmall} ${textColors.secondary}`}>Are you sure you want to cancel?</div>
      </Modal>

    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!loading) onClose?.();
      }}
      title="Activate your plan"
      // Wider on desktop so the modal can use a 2-column layout and avoid vertical scrolling.
      size="xl"
      bodyMaxHeight="calc(100vh - 160px)"
      closeOnOverlayClick={!loading}
      closeOnEsc={!loading}
      showCloseButton={!loading}
    >
      <div className="space-y-5">
        {notice ? <div>{notice}</div> : null}

        <div className="flex flex-wrap gap-2">
          <StepPill n="1" label="Confirm plan" />
          <StepPill n="2" label="Payment method" />
          <StepPill n="3" label="Activate" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* Left column: plan + tax location */}
          <div className="space-y-5 lg:col-span-6">
            <div className={`rounded-lg border ${borders.default} ${surfaces.secondary} p-4`}>
              <div className={`${typography.bodySmall} ${textColors.secondary}`}>Plan</div>
              <div className={`${typography.h5} ${textColors.primary}`}>
                {(planName || "").toUpperCase()}{" "}
                <span className={`${typography.bodySmall} ${textColors.secondary}`}>
                  ({billingPeriod === "yearly" ? "Annual" : "Monthly"})
                </span>
              </div>
              {couponCode ? (
                <div className={`${typography.caption} ${textColors.secondary} mt-1`}>
                  Coupon: <span className={`${textColors.primary} font-medium`}>{couponCode}</span>
                </div>
              ) : null}
              {started?.discount_applied?.type === "stacked" ? (
                <div className={`${typography.caption} ${textColors.tertiary} mt-1`}>Stacking is applied sequentially by Stripe.</div>
              ) : null}
            </div>

            <div className={`rounded-lg border ${borders.default} ${surfaces.secondary} p-4 space-y-3`}>
              <div className={`${typography.bodySmall} ${textColors.secondary}`}>Tax location</div>

              {!stripePromise ? (
                <div className={`${typography.bodySmall} ${textColors.secondary}`}>Loading payment form…</div>
              ) : (
                <Elements
                  stripe={stripePromise}
                  options={{
                    appearance: stripeAppearance,
                    // Use setup mode so we can collect address without a PaymentIntent client secret.
                    mode: "setup",
                    // Stripe Elements requires a currency string when `mode` is provided.
                    // We currently bill in USD; keep this stable to avoid re-initializing Elements.
                    currency: "usd",
                  }}
                >
                  <div className={`rounded-lg border ${borders.default} ${surfaces.primary} p-4`}>
                    <AddressElement
                      options={{
                        mode: "billing",
                        defaultValues: { address: { country: "US" } },
                        disabled: loading || Boolean(started),
                      }}
                      onChange={(e) => {
                        setTaxLocationTouched(true);
                        setTaxAddressComplete(Boolean(e?.complete));
                        setTaxAddressValue(e?.value || null);
                        if (startError) setStartError(null);
                      }}
                    />
                  </div>
                </Elements>
              )}

              {!hasTaxLocation && taxLocationTouched ? (
                <div className={`${typography.caption} text-red-600 dark:text-red-400`}>Enter your country and postal code to calculate taxes.</div>
              ) : null}
              {!hasTaxLocation && !taxLocationTouched ? (
                <div className={`${typography.caption} ${textColors.tertiary}`}>Taxes will be calculated after you enter your country and postal code.</div>
              ) : null}
              {started ? (
                <div className={`${typography.caption} ${textColors.tertiary}`}>To change this, close and reopen the modal.</div>
              ) : null}
            </div>

            <SafeError message={startError} />
          </div>

          {/* Right column: pricing + payment (sticky on desktop) */}
          <div className="space-y-5 lg:col-span-6 lg:sticky lg:top-0">
            {pricingNow ? (
              <div className={`rounded-lg border ${borders.default} ${surfaces.secondary} p-4 space-y-2`}>
                <div className={`${typography.bodySmall} ${textColors.secondary}`}>Price breakdown</div>

                <div className="space-y-1">
                  <div className="flex items-center justify-between gap-4">
                    <div className={`${typography.bodySmall} ${textColors.primary}`}>Base price</div>
                    <div className={`${typography.bodySmall} ${textColors.primary} font-medium`}>
                      {formatMoneyFromMinor(pricingNow.subtotal, pricingCurrency) || "—"}
                    </div>
                  </div>

                  {(pricingNow.discounts || []).map((d, idx) => {
                    const amt = formatMoneyFromMinor(d?.amount, pricingCurrency);
                    const label = String(d?.label || "Discount");
                    return (
                      <div key={`${d?.id || idx}`} className="flex items-center justify-between gap-4">
                        <div className={`${typography.bodySmall} ${textColors.secondary}`}>{label}</div>
                        <div className={`${typography.bodySmall} ${textColors.secondary}`}>{amt ? `- ${amt}` : "—"}</div>
                      </div>
                    );
                  })}

                  {pricingNow.tax_total != null ? (
                    <div className="flex items-center justify-between gap-4">
                      <div className={`${typography.bodySmall} ${textColors.secondary}`}>Tax</div>
                      <div className={`${typography.bodySmall} ${textColors.secondary}`}>
                        {formatMoneyFromMinor(pricingNow.tax_total, pricingCurrency) || "—"}
                      </div>
                    </div>
                  ) : null}

                  <div className={`mt-2 pt-2 border-t ${borders.default} flex items-center justify-between gap-4`}>
                    <div className={`${typography.bodySmall} ${textColors.primary} font-medium`}>Total due now</div>
                    <div className={`${typography.bodySmall} ${textColors.primary} font-semibold`}>
                      {formatMoneyFromMinor(pricingNow.amount_due, pricingCurrency) || "—"}
                    </div>
                  </div>
                </div>

                {laterPricing ? (
                  <div className={`mt-3 pt-3 border-t ${borders.default} space-y-1`}>
                    <div className={`${typography.caption} ${textColors.secondary}`}>
                      {trialEnd ? `After trial ends${formatUnixDate(trialEnd) ? ` (on ${formatUnixDate(trialEnd)})` : ""}` : "Next payment"}
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between gap-4">
                        <div className={`${typography.bodySmall} ${textColors.primary}`}>Base price</div>
                        <div className={`${typography.bodySmall} ${textColors.primary} font-medium`}>
                          {formatMoneyFromMinor(laterPricing.subtotal, pricingCurrency) || "—"}
                        </div>
                      </div>

                      {(laterPricing.discounts || []).map((d, idx) => {
                        const amt = formatMoneyFromMinor(d?.amount, pricingCurrency);
                        const label = String(d?.label || "Discount");
                        return (
                          <div key={`${d?.id || idx}`} className="flex items-center justify-between gap-4">
                            <div className={`${typography.bodySmall} ${textColors.secondary}`}>{label}</div>
                            <div className={`${typography.bodySmall} ${textColors.secondary}`}>{amt ? `- ${amt}` : "—"}</div>
                          </div>
                        );
                      })}

                      {laterPricing.tax_total != null ? (
                        <div className="flex items-center justify-between gap-4">
                          <div className={`${typography.bodySmall} ${textColors.secondary}`}>Tax</div>
                          <div className={`${typography.bodySmall} ${textColors.secondary}`}>
                            {formatMoneyFromMinor(laterPricing.tax_total, pricingCurrency) || "—"}
                          </div>
                        </div>
                      ) : null}

                      <div className={`mt-2 pt-2 border-t ${borders.default} flex items-center justify-between gap-4`}>
                        <div className={`${typography.bodySmall} ${textColors.primary} font-medium`}>
                          {trialEnd ? "Total after trial" : "Next payment total"}
                        </div>
                        <div className={`${typography.bodySmall} ${textColors.primary} font-semibold`}>
                          {formatMoneyFromMinor(laterPricing.amount_due, pricingCurrency) || "—"}
                        </div>
                      </div>
                    </div>
                    {trialEnd && laterPricing?.is_estimate ? (
                      <div className={`${typography.caption} ${textColors.tertiary}`}>Estimate (based on plan price and applied discounts).</div>
                    ) : null}
                    {firstInvoiceOnlyNote ? <div className={`${typography.caption} ${textColors.tertiary}`}>{firstInvoiceOnlyNote}</div> : null}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className={`rounded-lg border ${borders.default} ${surfaces.secondary} p-4`}>
                {loading ? (
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 animate-spin text-blue-500 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span className={`${typography.bodySmall} ${textColors.secondary}`}>
                      Calculating taxes and preparing payment…
                    </span>
                  </div>
                ) : (
                  <div className={`${typography.bodySmall} ${textColors.secondary}`}>
                    After you enter your tax location, we’ll calculate your total and show the payment form here.
                  </div>
                )}
              </div>
            )}

            <SafeError message={confirmError} />

            {successMessage ? (
              <div className={`rounded-lg border ${borders.default} ${surfaces.primary} p-4`}>
                <div className={`${typography.body} ${textColors.primary}`}>{successMessage}</div>
              </div>
            ) : showPaymentElement ? (
              !stripePromise ? (
                <div className={`${typography.bodySmall} text-red-600 dark:text-red-400`}>
                  Stripe configuration is missing. Set <code className="font-mono">REACT_APP_STRIPE_PUBLISHABLE_KEY</code> or configure{" "}
                  <code className="font-mono">STRIPE_PUBLISHABLE_KEY</code> in the backend and reload.
                </div>
              ) : (
                <Elements
                  stripe={stripePromise}
                  options={{
                    clientSecret: effectiveClientSecret,
                    appearance: stripeAppearance,
                  }}
                >
                  <SubscriptionPaymentForm
                    disabled={loading}
                    onRequestCancel={requestCancel}
                    confirmReturnUrl={window.location.href}
                    mode={mode}
                    onError={setConfirmError}
                    onSuccess={async () => {
                      setConfirmError(null);
                      setSuccessMessage(
                        mode === "setup" ? "Payment method saved. Activating your subscription…" : "Payment confirmed. Activating your subscription…"
                      );
                      try {
                        if (started?.subscription_id) {
                          await apiFetch(API_ENDPOINTS.STRIPE_SUBSCRIPTION_CONFIRM, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ subscription_id: started.subscription_id }),
                          });
                        }
                        const subResp = await apiFetch(API_ENDPOINTS.CURRENT_SUBSCRIPTION, { headers: { "Content-Type": "application/json" } });
                        const subData = await subResp.json().catch(() => null);
                        if (subResp.ok) onSubscriptionUpdated?.(subData);
                      } catch {}
                      onClose?.();
                      onActivated?.({ planName, billingPeriod });
                    }}
                  />
                </Elements>
              )
            ) : null}
          </div>
        </div>
      </div>
    </Modal>
    </>
  );
};

export default SubscriptionPaymentModal;

